import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';

const SECTEURS = ['Banque', 'Commerce', 'Industrie', 'Résidence', 'Bureau', 'Ambassade', 'Chantier'];
const CLOTURES = ['Mur', 'Grillage', 'Haie', 'Aucune'];
const ECLAIRAGES = ['Bon', 'Moyen', 'Faible', 'Aucun'];
const ECLAIRAGES_INT = ['Bon', 'Moyen', 'Faible'];
const ETATS = ['Bon', 'Moyen', 'Defaillant'];

const RISQUES = [
  { key: 'score_acces', label: 'Accès non sécurisés' },
  { key: 'score_zones_aveugles', label: 'Zones aveugles (sans visibilité)' },
  { key: 'score_eclairage', label: 'Éclairage insuffisant' },
  { key: 'score_surveillance', label: 'Absence de surveillance électronique' },
  { key: 'score_environnement', label: 'Environnement à risque (quartier)' },
  { key: 'score_valeur_biens', label: 'Valeur des biens à protéger' },
  { key: 'score_historique', label: 'Historique d\'incidents' },
  { key: 'score_personnel', label: 'Personnel non formé' },
  { key: 'score_flux', label: 'Flux de personnes/véhicules' },
  { key: 'score_risques_specifiques', label: 'Risques spécifiques (incendie, inondation)' },
];

function niveauRisque(score) {
  if (score <= 25) return { label: 'FAIBLE', color: '#22C55E', bg: '#F0FDF4' };
  if (score <= 50) return { label: 'MOYEN', color: '#F59E0B', bg: '#FFFBEB' };
  if (score <= 75) return { label: 'ÉLEVÉ', color: '#EF4444', bg: '#FEF2F2' };
  return { label: 'CRITIQUE', color: '#991B1B', bg: '#FEF2F2', blink: true };
}

export default function NouvelAudit() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [auditId, setAuditId] = useState(null);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    client_nom: '', raison_sociale: '', contact_nom: '', contact_telephone: '', contact_email: '',
    secteur_activite: '', date_visite: '', auditeur_id: '',
    adresse: '', latitude: '', longitude: '', superficie_m2: '', nb_batiments: 1, nb_etages: 1,
    nb_acces: 1, type_cloture: '', eclairage_exterieur: '', eclairage_interieur: '',
    parking: false, parking_places: 0, zone_stockage_sensible: false,
    gardiennage_actuel: false, gardiennage_nb_agents: 0, gardiennage_societe: '',
    cameras: false, cameras_nombre: 0, cameras_etat: '', alarme_intrusion: false, alarme_type: '', alarme_etat: '',
    controle_acces: false, controle_acces_type: '', detection_incendie: false, coffre_fort: false,
    historique_incidents: '',
    score_acces: 0, score_zones_aveugles: 0, score_eclairage: 0, score_surveillance: 0,
    score_environnement: 0, score_valeur_biens: 0, score_historique: 0, score_personnel: 0,
    score_flux: 0, score_risques_specifiques: 0,
  });

  useEffect(() => {
    api.get('/users').then(r => setUsers(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const scoreTotal = RISQUES.reduce((s, r) => s + (parseInt(form[r.key]) || 0), 0);
  const niveau = niveauRisque(scoreTotal);

  const saveStep = async () => {
    setSaving(true);
    try {
      if (!auditId) {
        const res = await api.post('/audits');
        setAuditId(res.data.id);
        await api.put(`/audits/${res.data.id}`, { ...form, statut: 'en_cours' });
      } else {
        await api.put(`/audits/${auditId}`, form);
      }
    } catch {}
    setSaving(false);
  };

  const nextStep = async () => {
    await saveStep();
    setStep(s => Math.min(5, s + 1));
  };

  const prevStep = () => setStep(s => Math.max(1, s - 1));

  const handleGenerate = async () => {
    setSaving(true);
    try {
      await api.put(`/audits/${auditId}`, { ...form, statut: 'termine' });
      await api.post(`/audits/${auditId}/plan`);
      navigate(`/audit/${auditId}/plan`);
    } catch {}
    setSaving(false);
  };

  const stepTitles = ['Identification', 'Caractéristiques du site', 'Sécurité existante', 'Évaluation des risques', 'Récapitulatif'];

  return (
    <div>
      {/* Progress bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24 }}>
        {stepTitles.map((t, i) => (
          <div key={i} style={{ flex: 1 }}>
            <div style={{
              height: 4, borderRadius: 2,
              background: i + 1 <= step ? '#8B1A1A' : '#E0E0E0',
              transition: 'background 300ms',
            }} />
            <div style={{
              fontSize: 10, fontWeight: i + 1 === step ? 700 : 500, marginTop: 4,
              color: i + 1 === step ? '#8B1A1A' : '#999',
              textTransform: 'uppercase', letterSpacing: 0.5,
            }}>{i + 1}. {t}</div>
          </div>
        ))}
      </div>

      <div className="content-card" style={{ padding: '1.5rem' }}>
        {/* ETAPE 1 */}
        {step === 1 && (
          <>
            <div className="gss-modal-section">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18"/></svg>
              <span>IDENTIFICATION DU CLIENT</span>
            </div>
            <div className="gss-form-grid">
              <div className="gss-field"><label>Nom du client *</label><input className="gss-input" value={form.client_nom} onChange={e => set('client_nom', e.target.value)} /></div>
              <div className="gss-field"><label>Raison sociale</label><input className="gss-input" value={form.raison_sociale} onChange={e => set('raison_sociale', e.target.value)} /></div>
              <div className="gss-field"><label>Contact (nom)</label><input className="gss-input" value={form.contact_nom} onChange={e => set('contact_nom', e.target.value)} /></div>
              <div className="gss-field"><label>Téléphone</label><input className="gss-input" value={form.contact_telephone} onChange={e => set('contact_telephone', e.target.value)} /></div>
              <div className="gss-field"><label>Email</label><input className="gss-input" type="email" value={form.contact_email} onChange={e => set('contact_email', e.target.value)} /></div>
              <div className="gss-field"><label>Secteur d'activité</label>
                <select className="gss-input" value={form.secteur_activite} onChange={e => set('secteur_activite', e.target.value)}>
                  <option value="">-- Sélectionner --</option>
                  {SECTEURS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="gss-field"><label>Date de visite</label><input className="gss-input" type="date" value={form.date_visite} onChange={e => set('date_visite', e.target.value)} /></div>
              <div className="gss-field"><label>Auditeur</label>
                <select className="gss-input" value={form.auditeur_id} onChange={e => set('auditeur_id', e.target.value)}>
                  <option value="">-- Sélectionner --</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.prenom} {u.nom}</option>)}
                </select>
              </div>
            </div>
          </>
        )}

        {/* ETAPE 2 */}
        {step === 2 && (
          <>
            <div className="gss-modal-section">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
              <span>CARACTÉRISTIQUES DU SITE</span>
            </div>
            <div className="gss-form-grid">
              <div className="gss-field gss-field-full"><label>Adresse complète</label><input className="gss-input" value={form.adresse} onChange={e => set('adresse', e.target.value)} /></div>
              <div className="gss-field"><label>Latitude</label><input className="gss-input" type="number" step="any" value={form.latitude} onChange={e => set('latitude', e.target.value)} /></div>
              <div className="gss-field"><label>Longitude</label><input className="gss-input" type="number" step="any" value={form.longitude} onChange={e => set('longitude', e.target.value)} /></div>
              <div className="gss-field"><label>Superficie (m²)</label><input className="gss-input" type="number" value={form.superficie_m2} onChange={e => set('superficie_m2', e.target.value)} /></div>
              <div className="gss-field"><label>Nombre de bâtiments</label><input className="gss-input" type="number" min="1" value={form.nb_batiments} onChange={e => set('nb_batiments', parseInt(e.target.value) || 1)} /></div>
              <div className="gss-field"><label>Nombre d'étages</label><input className="gss-input" type="number" min="1" value={form.nb_etages} onChange={e => set('nb_etages', parseInt(e.target.value) || 1)} /></div>
              <div className="gss-field"><label>Nombre d'accès</label><input className="gss-input" type="number" min="1" value={form.nb_acces} onChange={e => set('nb_acces', parseInt(e.target.value) || 1)} /></div>
              <div className="gss-field"><label>Type de clôture</label>
                <select className="gss-input" value={form.type_cloture} onChange={e => set('type_cloture', e.target.value)}>
                  <option value="">--</option>{CLOTURES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="gss-field"><label>Éclairage extérieur</label>
                <select className="gss-input" value={form.eclairage_exterieur} onChange={e => set('eclairage_exterieur', e.target.value)}>
                  <option value="">--</option>{ECLAIRAGES.map(e => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>
              <div className="gss-field"><label>Éclairage intérieur</label>
                <select className="gss-input" value={form.eclairage_interieur} onChange={e => set('eclairage_interieur', e.target.value)}>
                  <option value="">--</option>{ECLAIRAGES_INT.map(e => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>
              <div className="gss-field" style={{ display: 'flex', alignItems: 'end', gap: 12, paddingBottom: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.parking} onChange={e => set('parking', e.target.checked)} /> Parking
                </label>
                {form.parking && <input className="gss-input" type="number" style={{ width: 100 }} placeholder="Places" value={form.parking_places} onChange={e => set('parking_places', parseInt(e.target.value) || 0)} />}
              </div>
              <div className="gss-field" style={{ display: 'flex', alignItems: 'end', paddingBottom: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.zone_stockage_sensible} onChange={e => set('zone_stockage_sensible', e.target.checked)} /> Zone de stockage sensible
                </label>
              </div>
            </div>
          </>
        )}

        {/* ETAPE 3 */}
        {step === 3 && (
          <>
            <div className="gss-modal-section">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              <span>SÉCURITÉ EXISTANTE</span>
            </div>
            <div className="gss-form-grid">
              <div className="gss-field" style={{ display: 'flex', alignItems: 'end', paddingBottom: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.gardiennage_actuel} onChange={e => set('gardiennage_actuel', e.target.checked)} /> Gardiennage actuel
                </label>
              </div>
              {form.gardiennage_actuel && (
                <>
                  <div className="gss-field"><label>Nombre agents</label><input className="gss-input" type="number" value={form.gardiennage_nb_agents} onChange={e => set('gardiennage_nb_agents', parseInt(e.target.value) || 0)} /></div>
                  <div className="gss-field"><label>Société</label><input className="gss-input" value={form.gardiennage_societe} onChange={e => set('gardiennage_societe', e.target.value)} /></div>
                </>
              )}
              <div className="gss-field" style={{ display: 'flex', alignItems: 'end', paddingBottom: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.cameras} onChange={e => set('cameras', e.target.checked)} /> Caméras
                </label>
              </div>
              {form.cameras && (
                <>
                  <div className="gss-field"><label>Nombre</label><input className="gss-input" type="number" value={form.cameras_nombre} onChange={e => set('cameras_nombre', parseInt(e.target.value) || 0)} /></div>
                  <div className="gss-field"><label>État</label>
                    <select className="gss-input" value={form.cameras_etat} onChange={e => set('cameras_etat', e.target.value)}>
                      <option value="">--</option>{ETATS.map(e => <option key={e} value={e}>{e}</option>)}
                    </select>
                  </div>
                </>
              )}
              <div className="gss-field" style={{ display: 'flex', alignItems: 'end', paddingBottom: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.alarme_intrusion} onChange={e => set('alarme_intrusion', e.target.checked)} /> Alarme intrusion
                </label>
              </div>
              {form.alarme_intrusion && (
                <>
                  <div className="gss-field"><label>Type</label><input className="gss-input" value={form.alarme_type} onChange={e => set('alarme_type', e.target.value)} /></div>
                  <div className="gss-field"><label>État</label>
                    <select className="gss-input" value={form.alarme_etat} onChange={e => set('alarme_etat', e.target.value)}>
                      <option value="">--</option>{ETATS.map(e => <option key={e} value={e}>{e}</option>)}
                    </select>
                  </div>
                </>
              )}
              <div className="gss-field" style={{ display: 'flex', alignItems: 'end', paddingBottom: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.controle_acces} onChange={e => set('controle_acces', e.target.checked)} /> Contrôle d'accès
                </label>
              </div>
              {form.controle_acces && (
                <div className="gss-field"><label>Type</label><input className="gss-input" value={form.controle_acces_type} onChange={e => set('controle_acces_type', e.target.value)} placeholder="Badge, biometrique, code..." /></div>
              )}
              <div className="gss-field" style={{ display: 'flex', alignItems: 'end', paddingBottom: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.detection_incendie} onChange={e => set('detection_incendie', e.target.checked)} /> Détection incendie
                </label>
              </div>
              <div className="gss-field" style={{ display: 'flex', alignItems: 'end', paddingBottom: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.coffre_fort} onChange={e => set('coffre_fort', e.target.checked)} /> Coffre-fort
                </label>
              </div>
              <div className="gss-field gss-field-full"><label>Historique incidents</label>
                <textarea className="gss-input" rows={3} value={form.historique_incidents} onChange={e => set('historique_incidents', e.target.value)} placeholder="Incidents passés connus..." style={{ height: 'auto', padding: '10px 12px' }} />
              </div>
            </div>
          </>
        )}

        {/* ETAPE 4 */}
        {step === 4 && (
          <>
            <div className="gss-modal-section">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <span>ÉVALUATION DES RISQUES</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {RISQUES.map(r => (
                <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '10px 16px', background: '#FAFBFC', borderRadius: 10, border: '1px solid #F0F0F0' }}>
                  <div style={{ flex: 1, fontSize: 14, fontWeight: 500 }}>{r.label}</div>
                  <input type="range" min="0" max="10" value={form[r.key]} onChange={e => set(r.key, parseInt(e.target.value))}
                    style={{ width: 120, accentColor: '#8B1A1A' }} />
                  <div style={{ width: 36, textAlign: 'center', fontWeight: 700, fontSize: 16,
                    color: form[r.key] <= 3 ? '#22C55E' : form[r.key] <= 6 ? '#F59E0B' : '#EF4444' }}>
                    {form[r.key]}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
              <div style={{
                padding: '20px 40px', borderRadius: 16, textAlign: 'center',
                background: niveau.bg, border: `2px solid ${niveau.color}`,
                animation: niveau.blink ? 'lgBlink 1s ease-in-out infinite' : 'none',
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 2 }}>Score de vulnérabilité</div>
                <div style={{ fontSize: 48, fontWeight: 900, color: niveau.color, lineHeight: 1.2 }}>{scoreTotal}</div>
                <div style={{ fontSize: 12, color: '#666' }}>/ 100</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: niveau.color, marginTop: 4, letterSpacing: 1 }}>{niveau.label}</div>
              </div>
            </div>
          </>
        )}

        {/* ETAPE 5 */}
        {step === 5 && (
          <>
            <div className="gss-modal-section">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              <span>RÉCAPITULATIF DE L'AUDIT</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
              <div style={{ padding: '16px 32px', borderRadius: 16, textAlign: 'center', background: niveau.bg, border: `2px solid ${niveau.color}` }}>
                <div style={{ fontSize: 40, fontWeight: 900, color: niveau.color }}>{scoreTotal}/100</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: niveau.color }}>{niveau.label}</div>
              </div>
            </div>
            <div className="gss-form-grid">
              <div className="gss-detail-item"><label>Client</label><span>{form.client_nom || '-'}</span></div>
              <div className="gss-detail-item"><label>Raison sociale</label><span>{form.raison_sociale || '-'}</span></div>
              <div className="gss-detail-item"><label>Secteur</label><span>{form.secteur_activite || '-'}</span></div>
              <div className="gss-detail-item"><label>Date visite</label><span>{form.date_visite || '-'}</span></div>
              <div className="gss-detail-item"><label>Adresse</label><span>{form.adresse || '-'}</span></div>
              <div className="gss-detail-item"><label>Superficie</label><span>{form.superficie_m2 ? `${form.superficie_m2} m²` : '-'}</span></div>
              <div className="gss-detail-item"><label>Bâtiments / Étages</label><span>{form.nb_batiments} / {form.nb_etages}</span></div>
              <div className="gss-detail-item"><label>Accès</label><span>{form.nb_acces}</span></div>
              <div className="gss-detail-item"><label>Gardiennage</label><span>{form.gardiennage_actuel ? `Oui (${form.gardiennage_nb_agents} agents)` : 'Non'}</span></div>
              <div className="gss-detail-item"><label>Caméras</label><span>{form.cameras ? `Oui (${form.cameras_nombre})` : 'Non'}</span></div>
              <div className="gss-detail-item"><label>Alarme</label><span>{form.alarme_intrusion ? 'Oui' : 'Non'}</span></div>
              <div className="gss-detail-item"><label>Contrôle accès</label><span>{form.controle_acces ? 'Oui' : 'Non'}</span></div>
            </div>
            <div style={{ marginTop: 20 }}>
              <div className="gss-modal-section">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
                <span>FAILLES IDENTIFIÉES</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {RISQUES.filter(r => form[r.key] >= 6).map(r => (
                  <span key={r.key} style={{ background: form[r.key] >= 8 ? '#FEE2E2' : '#FEF3C7', color: form[r.key] >= 8 ? '#DC2626' : '#D97706', padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                    {r.label}: {form[r.key]}/10
                  </span>
                ))}
                {RISQUES.filter(r => form[r.key] >= 6).length === 0 && <span style={{ color: '#22C55E', fontSize: 13 }}>Aucune faille critique détectée</span>}
              </div>
            </div>
          </>
        )}

        {/* Navigation */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24, paddingTop: 16, borderTop: '1px solid #F0F0F0' }}>
          <button className="gss-btn-cancel" onClick={prevStep} disabled={step === 1}>
            Précédent
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            {step < 5 && (
              <button className="gss-btn-deploy" onClick={nextStep} disabled={saving}>
                {saving ? 'Sauvegarde...' : 'Suivant'}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            )}
            {step === 5 && (
              <button className="gss-btn-deploy" onClick={handleGenerate} disabled={saving}
                style={{ background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                {saving ? 'Génération...' : 'GÉNÉRER LE PLAN DE PRÉVENTION'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
